import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, FileBarChart, Calendar, Trash2 } from "lucide-react";
import { formatIDR, formatDate, formatApiError } from "../lib/format";
import { DIVISIONS, REPORT_TYPES, isManagement, divisionById, canDelete } from "../lib/constants";

function isoDate(d) { return d.toISOString().slice(0, 10); }
function weekRange(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay() || 7;
  const monday = new Date(now); monday.setDate(now.getDate() - day + 1 + offsetWeeks * 7);
  const nextMonday = new Date(monday); nextMonday.setDate(monday.getDate() + 7);
  return { start: isoDate(monday), end: isoDate(nextMonday) };
}
function monthRange(offsetMonths = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 1);
  return { start: isoDate(start), end: isoDate(end) };
}

export default function Reports() {
  const { user } = useAuth();
  const canCompany = isManagement(user);
  const isDivUser = ["division_manager", "division_admin"].includes(user?.role);
  const defaultDivision = user?.division_id || (DIVISIONS[0] && DIVISIONS[0].id);

  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [form, setForm] = useState({
    type: canCompany ? "weekly_company" : "weekly_division",
    ...weekRange(0),
    division_id: defaultDivision,
    label: "",
  });

  const load = async () => {
    const { data } = await api.get("/reports");
    setReports(data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try {
      const isDivType = form.type.endsWith("_division");
      const payload = { ...form, period_start: form.start, period_end: form.end };
      delete payload.start; delete payload.end;
      if (!isDivType) delete payload.division_id;
      const { data } = await api.post("/reports/generate", payload);
      toast.success("Laporan digenerate + snapshot tersimpan");
      setGenOpen(false); load();
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReport = async (r) => {
    const { data } = await api.get(`/reports/${r.id}`);
    setSelected(data);
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus snapshot laporan "${r.label}"?`)) return;
    await api.delete(`/reports/${r.id}`);
    toast.success("Snapshot dihapus"); load();
  };

  const quickPreset = (type) => {
    const isDiv = type.endsWith("_division");
    if (type.startsWith("weekly")) setForm({ type, ...weekRange(0), division_id: defaultDivision, label: `Mingguan ${weekRange(0).start}` });
    else setForm({ type, ...monthRange(0), division_id: defaultDivision, label: `Bulanan ${monthRange(0).start.slice(0, 7)}` });
  };

  const byType = useMemo(() => {
    const g = { weekly_company: [], monthly_company: [], weekly_division: [], monthly_division: [] };
    reports.forEach((r) => g[r.type]?.push(r));
    return g;
  }, [reports]);

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Laporan (Snapshot)</h1>
          <p className="text-sm text-[#5C6066] mt-1">Laporan Mingguan & Bulanan permanen — snapshot tersimpan, data historis tidak berubah.</p>
        </div>
        <Button className="bg-brand text-white" onClick={() => setGenOpen(true)} data-testid="btn-generate-report">
          <Plus className="w-4 h-4 mr-1.5" /> Generate Laporan
        </Button>
      </div>

      <Tabs defaultValue={canCompany ? "weekly_company" : "weekly_division"}>
        <TabsList className="flex-wrap h-auto">
          {canCompany && <TabsTrigger value="weekly_company" data-testid="tab-weekly-company">Mingguan Perusahaan</TabsTrigger>}
          {canCompany && <TabsTrigger value="monthly_company" data-testid="tab-monthly-company">Bulanan Perusahaan</TabsTrigger>}
          <TabsTrigger value="weekly_division" data-testid="tab-weekly-division">Mingguan Divisi</TabsTrigger>
          <TabsTrigger value="monthly_division" data-testid="tab-monthly-division">Bulanan Divisi</TabsTrigger>
        </TabsList>

        {Object.entries(byType).map(([t, list]) => (
          <TabsContent key={t} value={t} className="mt-4">
            {list.length === 0 ? (
              <Card className="p-10 border border-dashed border-[#D6CEC0] bg-white text-center text-[#8C9096]">
                <FileBarChart className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <div className="text-sm">Belum ada snapshot. Klik <b>Generate Laporan</b> untuk membuat.</div>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {list.map((r) => (
                  <Card key={r.id} className="p-4 border border-[#E5E0D8] bg-white hover:border-brand transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <Badge variant="outline" className="mb-2">{REPORT_TYPES[r.type]}</Badge>
                        <div className="font-display font-semibold">{r.label}</div>
                        <div className="text-xs text-[#5C6066] mt-1">{formatDate(r.period_start)} — {formatDate(r.period_end)}</div>
                        {r.division_id && <div className="text-xs text-[#8C9096] mt-0.5">{divisionById(r.division_id)?.name}</div>}
                        <div className="text-[11px] text-[#8C9096] mt-2">Snapshot: {new Date(r.generated_at).toLocaleString("id-ID")} oleh {r.generated_by_name}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="bg-brand text-white flex-1" onClick={() => openReport(r)} data-testid={`btn-view-report-${r.id}`}>Lihat Data</Button>
                      {canDelete(user) && <Button variant="outline" size="icon" onClick={() => remove(r)} data-testid={`btn-delete-report-${r.id}`}><Trash2 className="w-4 h-4"/></Button>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Generator */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg" data-testid="modal-generate-report">
          <DialogHeader><DialogTitle>Generate Laporan Baru</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Jenis Laporan</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="report-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {canCompany && <SelectItem value="weekly_company">Mingguan — Perusahaan</SelectItem>}
                  {canCompany && <SelectItem value="monthly_company">Bulanan — Perusahaan</SelectItem>}
                  <SelectItem value="weekly_division">Mingguan — Divisi</SelectItem>
                  <SelectItem value="monthly_division">Bulanan — Divisi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => quickPreset(form.type)}><Calendar className="w-3.5 h-3.5 mr-1"/> Preset {form.type.startsWith("weekly") ? "Minggu ini" : "Bulan ini"}</Button>
              <Button variant="outline" size="sm" onClick={() => form.type.startsWith("weekly") ? setForm({ ...form, ...weekRange(-1) }) : setForm({ ...form, ...monthRange(-1) })}>Periode Sebelumnya</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Mulai</Label><Input type="date" className="mt-1.5" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="report-start"/></div>
              <div><Label>Sampai (exclusive)</Label><Input type="date" className="mt-1.5" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="report-end"/></div>
            </div>
            {form.type.endsWith("_division") && (
              <div>
                <Label>Divisi</Label>
                <Select value={form.division_id} onValueChange={(v) => setForm({ ...form, division_id: v })} disabled={isDivUser}>
                  <SelectTrigger className="mt-1.5" data-testid="report-division"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Label (opsional)</Label><Input className="mt-1.5" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="cth: Laporan Mingguan MUA W7"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={generate} data-testid="btn-run-generate">Generate + Simpan Snapshot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Detail */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="modal-report-detail">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Badge variant="outline">{REPORT_TYPES[selected.type]}</Badge>
                <span>{selected.label}</span>
              </DialogTitle>
              <div className="text-xs text-[#8C9096]">
                Periode: {formatDate(selected.period_start)} — {formatDate(selected.period_end)}
                {selected.division_id && ` • Divisi: ${divisionById(selected.division_id)?.name}`}
                {" • "}Snapshot dibuat: {new Date(selected.generated_at).toLocaleString("id-ID")}
              </div>
            </DialogHeader>
            <ReportView data={selected.data} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <Card className="p-3 border border-[#E5E0D8] bg-white">
    <div className="text-[10px] uppercase tracking-wider text-[#8C9096]">{label}</div>
    <div className={`mt-1 font-mono text-sm font-bold ${tone === "success" ? "text-emerald-700" : tone === "danger" ? "text-rose-700" : "text-[#1E2022]"}`}>{value}</div>
  </Card>
);

const ReportView = ({ data }) => {
  if (!data) return null;
  const s = data.sales || {}, o = data.operations || {}, f = data.finance || {}, e = data.employees || {}, c = data.content || {};
  return (
    <Tabs defaultValue="overview">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="overview">Ringkasan</TabsTrigger>
        <TabsTrigger value="sales">Sales & Inquiry</TabsTrigger>
        <TabsTrigger value="ops">Operasional</TabsTrigger>
        <TabsTrigger value="finance">Keuangan</TabsTrigger>
        <TabsTrigger value="employees">Karyawan</TabsTrigger>
        <TabsTrigger value="content">Konten</TabsTrigger>
        {data.divisions?.length > 0 && <TabsTrigger value="divisions">Per Divisi</TabsTrigger>}
      </TabsList>

      <TabsContent value="overview" className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="New Bookings" value={s.new_bookings || 0} />
        <Stat label="Nilai Kontrak" value={formatIDR(s.total_contract_value || 0)} />
        <Stat label="Inquiry Total" value={s.inquiries_total || 0} />
        <Stat label="Konversi Inquiry" value={`${s.inquiry_conversion_rate || 0}%`} tone="success"/>
        <Stat label="Revenue Diterima" value={formatIDR(f.revenue_in || 0)} tone="success"/>
        <Stat label="Total Invoice" value={formatIDR(f.total_invoiced || 0)} />
        <Stat label="Vendor Cost" value={formatIDR(f.vendor_cost || 0)} tone="danger"/>
        <Stat label="Fee Crew Total" value={formatIDR(f.wage_total || 0)} tone="danger"/>
        <Stat label="Margin Estimasi" value={formatIDR(f.gross_margin_est || 0)} tone={f.gross_margin_est >= 0 ? "success" : "danger"}/>
        <Stat label="Konten Total" value={c.total || 0} />
      </TabsContent>

      <TabsContent value="sales" className="mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Inquiry" value={s.inquiries_total || 0} />
          <Stat label="Conversion Rate" value={`${s.inquiry_conversion_rate || 0}%`} tone="success"/>
          <Stat label="New Bookings" value={s.new_bookings || 0} />
          <Stat label="Contract Value" value={formatIDR(s.total_contract_value || 0)} />
        </div>
        <Card className="p-4 border border-[#E5E0D8] bg-white">
          <div className="text-sm font-semibold mb-2">Inquiry per Status</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(s.inquiries_by_status || {}).map(([k, v]) => (
              <div key={k} className="p-2 rounded bg-[#FAF6EE] border border-[#E5E0D8] text-sm flex justify-between"><span>{k}</span><span className="font-mono font-bold">{v}</span></div>
            ))}
          </div>
        </Card>
      </TabsContent>

      <TabsContent value="ops" className="mt-4 space-y-4">
        <Card className="p-4 border border-[#E5E0D8] bg-white">
          <div className="text-sm font-semibold mb-2">Booking per Status</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(o.bookings_by_status || {}).map(([k, v]) => (
              <div key={k} className="p-2 rounded bg-[#FAF6EE] border border-[#E5E0D8] text-sm flex justify-between"><span>{k}</span><span className="font-mono font-bold">{v}</span></div>
            ))}
          </div>
        </Card>
        <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E0D8] font-semibold text-sm">Daftar Booking Periode Ini ({(o.bookings_list || []).length})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-[#FAF6EE] text-xs uppercase text-[#8C9096]"><th className="text-left p-3">Kode</th><th className="text-left p-3">Event</th><th className="text-left p-3">Tanggal</th><th className="text-left p-3">Status</th></tr></thead>
            <tbody>{(o.bookings_list || []).length === 0 ? <tr><td colSpan={4} className="text-center p-6 text-[#8C9096]">-</td></tr> :
              (o.bookings_list || []).map((b) => <tr key={b.id} className="border-t border-[#F0EBE0]"><td className="p-3 font-mono text-xs text-brand">{b.code}</td><td className="p-3">{b.event_name}</td><td className="p-3">{formatDate(b.event_date)}</td><td className="p-3">{b.status}</td></tr>)}
            </tbody>
          </table>
        </Card>
      </TabsContent>

      <TabsContent value="finance" className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Revenue In" value={formatIDR(f.revenue_in || 0)} tone="success"/>
        <Stat label="Invoice Terbit" value={f.invoices_issued || 0} />
        <Stat label="Total Invoice" value={formatIDR(f.total_invoiced || 0)} />
        <Stat label="Vendor Cost" value={formatIDR(f.vendor_cost || 0)} tone="danger"/>
        <Stat label="Vendor Terbayar" value={formatIDR(f.vendor_paid || 0)} tone="danger"/>
        <Stat label="Fee Crew Total" value={formatIDR(f.wage_total || 0)} />
        <Stat label="Fee Terbayar" value={formatIDR(f.wage_paid || 0)} tone="danger"/>
        <Stat label="Margin Estimasi" value={formatIDR(f.gross_margin_est || 0)} tone={f.gross_margin_est >= 0 ? "success" : "danger"}/>
      </TabsContent>

      <TabsContent value="employees" className="mt-4">
        <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E0D8] font-semibold text-sm">Top Karyawan by Fee ({e.count || 0})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-[#FAF6EE] text-xs uppercase text-[#8C9096]"><th className="text-left p-3">Nama</th><th className="text-left p-3">Role</th><th className="text-left p-3">Divisi</th><th className="text-right p-3">Jobs</th><th className="text-right p-3">Total Fee</th><th className="text-right p-3">Dibayar</th></tr></thead>
            <tbody>{(e.breakdown || []).length === 0 ? <tr><td colSpan={6} className="text-center p-6 text-[#8C9096]">-</td></tr> :
              (e.breakdown || []).map((emp, i) => <tr key={i} className="border-t border-[#F0EBE0]"><td className="p-3 font-medium">{emp.name}</td><td className="p-3">{emp.role}</td><td className="p-3">{divisionById(emp.division_id)?.short || "-"}</td><td className="p-3 text-right">{emp.jobs}</td><td className="p-3 text-right font-mono">{formatIDR(emp.total)}</td><td className="p-3 text-right font-mono text-emerald-700">{formatIDR(emp.paid)}</td></tr>)}
            </tbody>
          </table>
        </Card>
      </TabsContent>

      <TabsContent value="content" className="mt-4 space-y-3">
        <Stat label="Total Konten" value={c.total || 0} />
        <Card className="p-4 border border-[#E5E0D8] bg-white">
          <div className="text-sm font-semibold mb-2">Konten per Status</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(c.by_status || {}).map(([k, v]) => (
              <div key={k} className="p-2 rounded bg-[#FAF6EE] border border-[#E5E0D8] text-sm flex justify-between"><span>{k}</span><span className="font-mono font-bold">{v}</span></div>
            ))}
          </div>
        </Card>
      </TabsContent>

      {data.divisions?.length > 0 && (
        <TabsContent value="divisions" className="mt-4">
          <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#FAF6EE] text-xs uppercase text-[#8C9096]"><th className="text-left p-3">Divisi</th><th className="text-right p-3">Bookings</th><th className="text-right p-3">Revenue Est.</th><th className="text-right p-3">Wage Total</th><th className="text-right p-3">Vendor Cost</th></tr></thead>
              <tbody>{data.divisions.map((d) => <tr key={d.division_id} className="border-t border-[#F0EBE0]"><td className="p-3 font-medium">{d.division_name}</td><td className="p-3 text-right">{d.bookings}</td><td className="p-3 text-right font-mono">{formatIDR(d.revenue_est)}</td><td className="p-3 text-right font-mono">{formatIDR(d.wage_total)}</td><td className="p-3 text-right font-mono">{formatIDR(d.vendor_cost)}</td></tr>)}</tbody>
            </table>
          </Card>
        </TabsContent>
      )}
    </Tabs>
  );
};
